import './notifications.css';
import { Button, Nav, Modal, OverlayTrigger, Tooltip, Alert, Table } from 'react-bootstrap';
import { useState, useContext, useEffect } from 'react';
import { useAppSelector } from "../../redux/hooks";
import { lddGlobalSessionInfo } from '../navigationTypes'
import { NormalizedNotification } from './notificationsTypes';
import { ActivityCenterContext } from "../../Context/ActivityCenter"
import * as NotiFuncs from './notificationsFuncs';
import Notification from './Notification';
import BulletinNotification from './BulletinNotification';
import {PaginationControl} from 'react-bootstrap-pagination-control';
import { useDispatch } from 'react-redux';
import { lddStateObject, setInboxState, setShowNotificationCentre } from '../../redux/reducer';

export function Notifications() {
    const [showBulletinDetails, setShowBulletinDetails] = useState(false);
    const [detailedBulletin, setDetailedBulletin] = useState<NormalizedNotification | null>();
    const [bulletins, setBulletins] = useState<NormalizedNotification[]>([]);
    const [notifications, setNotifications] = useState<NormalizedNotification[][]>([]);
    const [allReadButtons, setAllReadButtons] = useState(false);
    const sessionInfo: lddGlobalSessionInfo = useAppSelector((state) => state.SessionInfo);
    const show: boolean = useAppSelector((state: lddStateObject) => state.ShowNotificationCentre);
    const [page, setPage] = useState(1)
    const dispatch = useDispatch();

    const activityCenter = useContext(ActivityCenterContext);

    useEffect(() => {
        updateBulletinNotifications();
        updateNotifications();
    }, [show])

    const handleClose = () => {
        dispatch(setShowNotificationCentre(false));
    };

    const handleShow = () => {
        dispatch(setShowNotificationCentre(true));
    };
    const handleBulletinDetailClose = () => { setShowBulletinDetails(false) }

    const updateNotifications = async () => {
        const mainNotifs = await NotiFuncs.getMainNotifications(sessionInfo);
        if (mainNotifs.length) {
            setNotifications(mainNotifs);
        }
    }

    const updateBulletinNotifications = async () => {
        const bulletins = await NotiFuncs.getBulletins();
        if (bulletins && bulletins.length > 0) {
            await NotiFuncs.checkReadBulletins(sessionInfo.sid, bulletins[0].id);
            setBulletins(bulletins);
        }
    }

    const markCCAsRead = async (notification: NormalizedNotification): Promise<boolean> => {
        const success = await NotiFuncs.markNotificationAsRead(sessionInfo, notification);
        if (success) {
            notification.read = true;
            NotiFuncs.updateNotificationIndicator(notifications);
        }
        return success;
    }

    // On Load
    useEffect(() => {
        updateBulletinNotifications();
        updateNotifications();        
    }, [])

    useEffect(() => {
        NotiFuncs.updateNotificationIndicator(notifications);
        // periodic
        let interval = setInterval(async () => {
            const response = await NotiFuncs.getNewClientConnectNotifications(NotiFuncs.flattenGroupedNotifications(notifications), sessionInfo);
            const drNewNotifCount = await NotiFuncs.getNewDRNotificationCount(NotiFuncs.flattenGroupedNotifications(notifications), sessionInfo);
            if ((response.ok && response.result > 0) || drNewNotifCount > 0) {
                await updateBulletinNotifications();
                await updateNotifications();
                NotiFuncs.addNotificationAlertToActivityCenter((response.result + drNewNotifCount), activityCenter.Add, notifications);
            }
        }, 5 * 60 * 1000); // 5 min = 5 * 60 * 1000
        return () => clearInterval(interval);
    }, [notifications])

    async function handleClickUnlinkedBuilderMsg(notif: NormalizedNotification) {
        dispatch(setInboxState("Click", notif.msgID));
    }

    return (
        <>
            <OverlayTrigger placement="bottom" delay={200} overlay={<Tooltip>Notifications</Tooltip>}>
                <Nav.Item className="item" onClick={handleShow}>
                    <Nav.Link eventKey="link-4">
                        <i className="fa-duotone fa-bell fa-indicator"></i>
                        <span className='indicator indicator-warn' id='lddNotificationBadgeMainBarNew'></span>
                        <span className='indicator indicator-warn' id='lddNotificationBadgeMainBar'></span>
                    </Nav.Link>
                </Nav.Item>
            </OverlayTrigger>

            <Modal show={show} onShow={()=> {NotiFuncs.setReadBulletins(sessionInfo.sid, bulletins[0]?.id)}} onHide={handleClose} className="modal-settings modal-list modal-notifications" backdropClassName="offcanvas-backdrop">
                <Modal.Header closeButton>
                    <Modal.Title as={'h1'}>Notifications</Modal.Title>

                    <Button
                        variant="link-text"
                        style={{ marginLeft: "20px" }}
                        onClick={async () => {
                            await NotiFuncs.markAllNotificationsAsRead(sessionInfo, NotiFuncs.flattenGroupedNotifications(notifications))
                            updateNotifications();
                            setAllReadButtons(true)
                        }}
                    >
                        <i className="fa-regular fa-check-double"></i>
                        Mark all as read
                    </Button>
                </Modal.Header>

                <Modal.Body>
                    <BulletinNotification
                        bulletins={bulletins}
                        setShowBulletinDetails={setShowBulletinDetails}
                        setDetailedBulletin={setDetailedBulletin}
                        detailedBulletin={detailedBulletin}
                        showBulletinDetails={showBulletinDetails}
                        handleBulletinDetailClose={handleBulletinDetailClose}
                    />

                    <div className="notification-container">
                        <Table className="notification">
                            <NotificationTableHeader />
                            <tbody>
                                {
                                    notifications.length !== 0 ?
                                        notifications.slice(process.env.REACT_APP_NOTIF_PAGE_SIZE * (page - 1), process.env.REACT_APP_NOTIF_PAGE_SIZE * page).map((notif, index) => {
                                            return (
                                                <Notification
                                                    data={notif}
                                                    key={"Notif-" + index}
                                                    markCCAsRead={markCCAsRead}
                                                    type={'modal'}
                                                    handleClose={handleClose}
                                                    allReadButtons={allReadButtons}
                                                    handleClickUnlinkedBuilderMsg={handleClickUnlinkedBuilderMsg}
                                                />

                                            )
                                        })
                                        :
                                        <tr>
                                            <td colSpan={7}>
                                                <Alert style={{ margin: "0" }}>
                                                    No notifications to display.
                                                </Alert>
                                            </td>
                                        </tr>
                                }
                            </tbody>
                        </Table>

                        <PaginationControl
                            page={page}
                            between={4}
                            total={notifications.length}
                            limit={process.env.REACT_APP_NOTIF_PAGE_SIZE}
                            changePage={(page) => {
                                setPage(page);
                            }}
                            ellipsis={1}
                        />    
                    </div>
                </Modal.Body>
            </Modal>
        </>
    )
}

const NotificationTableHeader = () => {
    return (
        <thead>
            <tr>
                <th style={{ width: '40px' }}></th>
                <th style={{ width: '120px' }}>Type</th>
                <th style={{ width: '190px' }}>File No.</th>
                <th>Notification</th>
                <th style={{ width: '100px' }}>Date</th>
                <th style={{ width: '40px' }}></th>
                <th style={{ width: '40px' }}></th>
            </tr>
        </thead>
    )
}
